import { Controller } from '@hotwired/stimulus';
import consumer from '../../../../javascript/channels/consumer'
import { StagedUploadBatches, StagedUploadBatchSnapshot, StagedUploadBatchesConfig } from './staged_upload_batches';

declare var $: any;

declare const NProgress: {
  start: () => void;
  done: () => void;
};

// Mirrors AgGridProvisionComponent's FieldCentricError so `handsontable:errors` payloads match.
interface FieldCentricError {
  rows: number[];
  field: string;
  message: string;
}

const ATTACHMENT_COLUMNS = ["information_file", "choice_image", "choice_pdf"] as const;

// Fires on this controller's root element on every staged-file-input change, for the
// grid's attachment autocomplete.
const STAGED_FILES_CHANGED_EVENT = "api-dashboard:staged-files-changed";

// Not String#trim, which also strips NBSP/BOM; the server keeps those.
const ASCII_TRIM = /^[ \t\r\n]+|[ \t\r\n]+$/g;

interface SkippedEntry {
  name: string;
  reason: string;
}

// Update::PlanPresenter's body (update.rb:43, dry_run.rb:17) -- the dry-run and the
// write it precedes share this shape, differing only in `message`.
interface DryRunRow {
  row: number;
  question_id: number | null;
  answer_id: number | null;
  vote_id?: number | null;
  action: string;
  changes: string[];
}

interface DryRunResult {
  summary?: Record<string, number>;
  rows?: DryRunRow[];
  consequences?: Record<string, unknown>[];
}

// One DOM node per uploaded row is unbounded, so this caps *changed* rows only (noop rows
// never reach the list); summary/consequences are already bounded and stay uncapped.
const DRY_RUN_ROW_RENDER_CAP = 200;

// One error entry can aggregate every row sharing a vote_id (update/plan.rb resolve_elections),
// so the row list inside an entry needs its own bound, not just the entry count.
const ERROR_ROW_NUMBER_RENDER_CAP = 10;

// Windows/OneDrive/macOS metadata that appears in ordinary folders.
const IGNORED_FOLDER_ENTRIES = new Set(["desktop.ini", "thumbs.db", ".ds_store", "icon\r"]);

// `accept` is ignored by directory pickers, so this is the real filter.
const STAGED_FOLDER_EXTENSIONS = new Set(["jpg", "jpeg", "png", "pdf"]);

// Per-file references into a batch chip's cloned template controls, so batch snapshots
// can update them without a full re-render of the chip list.
interface BatchChipRefs {
  stateEl: HTMLElement;
  progressEl: HTMLElement;
  progressBarEl: HTMLElement;
  retryBtn: HTMLButtonElement;
  removeBtn: HTMLButtonElement;
}

export default class extends Controller {
  static targets = [
    "errorMessages", "errorMessagesSingle", "fileInput", "removeButton",
    "languagePreloadVoteId", "languagePickerSection", "languagePreloadSelect",
    "voteIdInput", "languageCreateSelect",
    "downloadSubmitButton", "importantText", "stagedFilesInput", "stagedFilesList",
    "stagedFilesCapsMessage", "stagedFilesSummary", "stagedFilesClear", "stagingTokenInput",
    "stagedFilesBatchCancel", "stagedFilesBatchChipTemplate", "dryRunPanel"
  ];

  static values = {
    maxFiles: Number,
    maxTotalBytes: Number,
    maxFileBytes: Number,
    // Batched-transport flag + caps/paths, read from the component's
    // data values (which read Questions::Provision::StagedFiles -- never restated here).
    stagingEnabled: Boolean,
    batchMaxFiles: Number,
    batchMaxBytes: Number,
    sessionMaxFiles: Number,
    sessionMaxTotalBytes: Number,
    batchConcurrency: Number,
    sessionExpiry: Number,
    stagingOpenPath: String,
    stagingBatchesPath: String,
    stagingFilesPath: String,
    stagingCancelPath: String
  };

  declare readonly errorMessagesTarget: HTMLElement;
  declare readonly errorMessagesSingleTarget: HTMLElement;
  declare readonly hasErrorMessagesSingleTarget: boolean;
  declare readonly fileInputTarget: HTMLInputElement;
  declare readonly removeButtonTarget: HTMLButtonElement;
  declare readonly stagedFilesInputTargets: HTMLInputElement[];
  declare readonly stagedFilesListTargets: HTMLElement[];
  declare readonly stagedFilesCapsMessageTargets: HTMLElement[];
  declare readonly stagedFilesSummaryTargets: HTMLElement[];
  declare readonly stagedFilesClearTargets: HTMLElement[];
  declare readonly stagingTokenInputTargets: HTMLInputElement[];
  declare readonly stagedFilesBatchCancelTargets: HTMLButtonElement[];
  declare readonly stagedFilesBatchChipTemplateTargets: HTMLTemplateElement[];
  declare readonly dryRunPanelTarget: HTMLElement;
  declare readonly hasDryRunPanelTarget: boolean;

  declare readonly languagePreloadVoteIdTarget: HTMLInputElement;
  declare readonly hasLanguagePreloadVoteIdTarget: boolean;
  declare readonly languagePickerSectionTarget: HTMLElement;
  declare readonly hasLanguagePickerSectionTarget: boolean;
  declare readonly languagePreloadSelectTarget: HTMLSelectElement;
  declare readonly hasLanguagePreloadSelectTarget: boolean;
  declare readonly voteIdInputTarget: HTMLInputElement;
  declare readonly hasVoteIdInputTarget: boolean;
  declare readonly languageCreateSelectTarget: HTMLSelectElement;
  declare readonly hasLanguageCreateSelectTarget: boolean;
  declare readonly downloadSubmitButtonTarget: HTMLElement;
  declare readonly hasDownloadSubmitButtonTarget: boolean;
  declare readonly importantTextTarget: HTMLElement;
  declare readonly hasImportantTextTarget: boolean;

  declare readonly maxFilesValue: number;
  declare readonly maxTotalBytesValue: number;
  declare readonly maxFileBytesValue: number;
  declare readonly stagingEnabledValue: boolean;
  declare readonly batchMaxFilesValue: number;
  declare readonly batchMaxBytesValue: number;
  declare readonly sessionMaxFilesValue: number;
  declare readonly sessionMaxTotalBytesValue: number;
  declare readonly batchConcurrencyValue: number;
  declare readonly sessionExpiryValue: number;
  declare readonly stagingOpenPathValue: string;
  declare readonly stagingBatchesPathValue: string;
  declare readonly stagingFilesPathValue: string;
  declare readonly stagingCancelPathValue: string;

  subscription: any = null;
  loading_subscription: any = null;
  uploadId: any = null;
  currentUploadMethod: string = 'handsontable';

  // Dynamic language-columns picker state (language-specific).
  private baseHandsontableColumns: Array<{ key: string; header: string }> = [];
  private currentLanguageColumns: Array<{ key: string; header: string }> = [];
  // Whether Load Table has revealed the grid/submit for the active tab; reset per frame nav.
  private tableRevealed = false;

  // Keyed per file input so the CSV and grid sections stay independent; holds the current
  // folder pick so `input.files` can be rebuilt after every pick or removal.
  private stagedFileSets: Map<HTMLInputElement, File[]> = new Map();
  private stagedFolderNames: Map<HTMLInputElement, string | null> = new Map();
  stagedNames: Set<string> = new Set();
  private lastGatheredRows: Record<string, string>[] = [];
  private stagedFilesChangeListener = (event: Event) => this.handleStagedFilesChanged(event);

  // One StagedUploadBatches session per staged-files input, plus the DOM
  // refs its chips need to reflect per-batch state without a full list re-render.
  private batchTransports: Map<HTMLInputElement, StagedUploadBatches> = new Map();
  private batchChipRefs: Map<HTMLInputElement, Map<string, BatchChipRefs>> = new Map();
  // An input that fell back to the same-request path after an open/batch failure --
  // its chips stop showing batch controls until the next fresh pick.
  private degradedInputs: Set<HTMLInputElement> = new Set();
  private batchCancelListener = (event: Event) => this.handleBatchCancelClick(event);

  turboFrameLoadListener: (event: Event) => void = () => {};

  connect() {
    const controller = this;

    this.handleHandsontableData = this.handleHandsontableData.bind(this);

    this.element.addEventListener("handsontable:data", this.handleHandsontableData);

    this.turboFrameLoadListener = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.id === "hot-frame") {
        // Reset revealed state on every frame navigation (API/type changes)
        this.tableRevealed = false;
        this.injectDropdownWhenReady();
      }
    };

    document.addEventListener("turbo:frame-load", this.turboFrameLoadListener);

    // turbo:frame-load only fires on frame *navigations* (category/type switches),
    // not for a turbo-frame that's already part of the initial full-page HTML.
    this.injectDropdownWhenReady();

    this.subscription = consumer.subscriptions.create( 
      { channel: 'UploadChannel' },
      {
        connected() {
        },

        disconnected() {
        },

        received(data: { status: string; errors: any; upload_id: any; }) {
          if (data.status === 'error') {
            if (!controller.errorMessagesTarget || !data.errors) return;
              const errorMessage = controller.currentUploadMethod === 'csv'
                ? 'Please fix the errors in the csv, download the error report for details.'
                : 'Please fix the errors in the table, download the error report for details.';
              controller.showErrorMessages(errorMessage);
              controller.uploadId = data.upload_id;
              controller.showDownloadErrorOptions();

              if (controller.currentUploadMethod === 'handsontable') {
                controller.dispatchHandsontableErrors(data.errors);
              }
          } else if (data.status === 'success') {
            controller.showSuccessMessage(controller.uploadSuccessHeading());
            controller.dispatchHandsontableErrors([]);
          }
        }
      }
    );

    this.loading_subscription = consumer.subscriptions.create(
      { channel: 'UploadLoadingIndicatorChannel' },
      {
        connected() {
        },

        disconnected() {
        },

        received(data: { status: "start" | "done" }) {
          if (data.status === "start") {
            NProgress.start();
          } else {
            NProgress.done();
          }
        },
      }
    );
  }


  disconnect() {
    this.element.removeEventListener("handsontable:data", this.handleHandsontableData);
    document.removeEventListener("turbo:frame-load", this.turboFrameLoadListener);

    if (this.subscription) {
      console.log("Unsubscribing from CsvUploadChannel");
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    if (this.loading_subscription) {
      console.log("Unsubscribing from ApiDashboardLoadingIndicatorChannel");
      this.loading_subscription.unsubscribe();
      this.loading_subscription = null;
    }
  }

  triggerGather() {
    document.dispatchEvent(new Event("gatherHandsontableData"));
  }

  handleHandsontableData(event: any) {
    const payloadField = this.element.querySelector<HTMLInputElement>('#handsontable-payload');

    if (payloadField) {
      payloadField.value = JSON.stringify(event.detail)
    }

    this.lastGatheredRows = (event.detail?.rows ?? []) as Record<string, string>[];
  }

submitForm(event: SubmitEvent) {
  this.clearErrorMessages();
  this.hideDryRunPanel();

  if (this.currentUploadMethod === 'csv') {
    const fileInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="csv_file"]');
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showErrorMessages('No file uploaded. Please upload your file before validation.');
      return;
    }
  }

  if (this.stagingEnabledValue && this.blockSubmitForPendingBatches()) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  document.dispatchEvent(new Event("gatherHandsontableData"));

  if (this.currentUploadMethod === 'handsontable' && this.runGridAttachmentPreCheck()) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const form = event.target as HTMLFormElement;
}

  submitSingleForm(event: MouseEvent) {
    event.preventDefault();
        
    if (this.hasErrorMessagesSingleTarget) {
      this.clearSingleErrorMessages();
    }

    const button = event.currentTarget as HTMLButtonElement;
    const form = button.closest('form') as HTMLFormElement;
    if (!form) return;

    const url = new URL(form.action);
    const formData = new FormData(form);
    
    formData.forEach((value, key) => {
      url.searchParams.set(key, value as string);
    });

    fetch(url.toString())
      .then(async response => {
        if (!response.ok) {
          let message = 'An error occurred';
          try {
            const data: { error?: string } = await response.json();
            message = data.error || message;
          } catch (_) { /* non-JSON error body */ }
          this.showSingleErrorMessage(message);
          return;
        }

        if (response.status === 202) {
          const data: { report_center_url?: string; message?: string } = await response.json();
          this.showSingleQueuedMessage(data.message || 'Export queued.', data.report_center_url);
          return;
        }

        const blob = await response.blob();
        const filename = this.filenameFromContentDisposition(response.headers.get('Content-Disposition')) || 'download.csv';
        this.triggerBlobDownload(blob, filename);
        this.showSingleSuccessMessage('Download successful!');
      })
      .catch(() => {
        this.showSingleErrorMessage('An unexpected error occurred');
      });
  }

  /** Large-list export: report_center_url links to the Report Queue page, not a file. */
  private showSingleQueuedMessage(message: string, reportCenterUrl?: string) {
    this.showSingleSuccessMessage(message);
    if (!reportCenterUrl || !this.hasErrorMessagesSingleTarget) return;

    const link = document.createElement('a');
    link.href = reportCenterUrl;
    link.textContent = 'Open Report Center';
    this.errorMessagesSingleTarget.append(' ', link);
  }

  /** Parses `filename="..."` (or unquoted) from a Content-Disposition header value. */
  private filenameFromContentDisposition(headerValue: string | null): string | null {
    if (!headerValue) return null;
    const match = headerValue.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    if (!match) return null;
    try { return decodeURIComponent(match[1]); } catch (_) { return match[1]; }
  }

  private triggerBlobDownload(blob: Blob, filename: string) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke after the click has been dispatched so the download request can still resolve the URL.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  clearSingleErrorMessages() {
    if (this.hasErrorMessagesSingleTarget) {
      this.errorMessagesSingleTarget.innerHTML = '';
    }
  }

  showSingleErrorMessage(message: string) {
    if (this.hasErrorMessagesSingleTarget) {
      this.errorMessagesSingleTarget.classList.remove('success-messages');
      this.errorMessagesSingleTarget.classList.add('error-messages');
      this.errorMessagesSingleTarget.innerHTML = message;
    }
  }

  showSingleSuccessMessage(message: string) {
    if (this.hasErrorMessagesSingleTarget) {
      this.errorMessagesSingleTarget.classList.remove('error-messages');
      this.errorMessagesSingleTarget.classList.add('success-messages');
      this.errorMessagesSingleTarget.innerHTML = message;
    }
  }

  handleSuccess(event: any) {
    const form = event.target as HTMLFormElement;

    const isHandsontable = form.classList.contains("handsontable-form");
    const isCsvUpload = form.classList.contains("csv-upload-form");

    if (!isHandsontable && !isCsvUpload) return;

    this.errorMessagesTarget.innerText = '';

    if (isHandsontable) {
      this.dispatchHandsontableErrors([]);
    }
  }

  handleError(event: any) {
    const form = event.target as HTMLFormElement;
    const [data] = event.detail;
    this.renderSubmitErrors(form, data);
  }

  // validateOnly's 422 reuses this -- the one error surface, never a second one.
  private renderSubmitErrors(form: HTMLFormElement, data: any) {
    const isHandsontable = form.classList.contains("handsontable-form");
    const isCsvUpload = form.classList.contains("csv-upload-form");

    if (!isHandsontable && !isCsvUpload) return;

    const errors = data?.errors ?? [];

    if (isHandsontable) {
      this.dispatchHandsontableErrors(errors);
    }

    // A dry run deliberately writes no UploadRecord, so there is no error report to point at --
    // the response body is the only detail this path will ever carry.
    if (!data?.upload_id) {
      this.renderInlineSubmitErrors(errors);
      return;
    }

    const errorMessage = this.currentUploadMethod === 'csv'
      ? 'Please fix the errors in the csv, download the error report for details.'
      : 'Please fix the errors in the table, download the error report for details.';
    this.showErrorMessages(errorMessage);

    this.uploadId = data.upload_id;
    this.showDownloadErrorOptions();
  }

  // Built as nodes with textContent, never an HTML string: `message` interpolates pasted cell
  // values and titles, and the error surface assigns innerHTML (showErrorMessages).
  private renderInlineSubmitErrors(errors: any) {
    if (!this.errorMessagesTarget) return;

    const entries = Array.isArray(errors)
      ? errors
      : [errors].filter(entry => entry !== null && entry !== undefined && entry !== '');

    if (entries.length === 0) {
      this.showErrorMessages('Validation failed, but the response carried no details.');
      return;
    }

    this.showErrorMessages(`${entries.length} Error${entries.length === 1 ? '' : 's'}`);

    const list = document.createElement('ul');
    list.className = 'validation-errors';

    entries.slice(0, DRY_RUN_ROW_RENDER_CAP).forEach(entry => {
      const item = document.createElement('li');
      item.className = 'validation-errors__item';
      item.textContent = this.describeSubmitError(entry);
      list.appendChild(item);
    });
    this.errorMessagesTarget.appendChild(list);

    const hidden = entries.length - DRY_RUN_ROW_RENDER_CAP;
    if (hidden > 0) {
      const remainder = document.createElement('div');
      remainder.className = 'validation-errors__remainder';
      remainder.textContent = `…and ${hidden} more error${hidden === 1 ? '' : 's'}`;
      this.errorMessagesTarget.appendChild(remainder);
    }
  }

  // Header-level errors carry `rows: []` and get no row prefix. Some endpoints send bare
  // strings instead of the field-centric hash, and one entry can span every row of a vote_id.
  private describeSubmitError(entry: any): string {
    if (typeof entry !== 'object' || entry === null) return String(entry);

    const rows = Array.isArray(entry.rows) ? entry.rows : [];
    const shown = rows.slice(0, ERROR_ROW_NUMBER_RENDER_CAP).join(', ');
    const overflow = rows.length - ERROR_ROW_NUMBER_RENDER_CAP;
    const rowPrefix = rows.length > 0
      ? `Row${rows.length === 1 ? '' : 's'} ${shown}${overflow > 0 ? ` +${overflow} more` : ''} — `
      : '';
    const fieldPrefix = entry.field ? `${entry.field}: ` : '';

    return `${rowPrefix}${fieldPrefix}${entry.message ?? entry.code ?? 'Invalid value'}`;
  }

  // rails-ujs only rewrites form tokens to this global one on DOMContentLoaded, which a
  // turbo-frame tab swap never fires -- so any in-frame fetch must read the meta tag itself.
  private csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? '';
  }

  // POSTs the same FormData a real submit would send, to url_validate, rendering
  // Update::DryRun's body via a separate fetch, not the submit forms' own ajax wiring.
  validateOnly(event: MouseEvent) {
    event.preventDefault();
    this.clearErrorMessages();
    this.hideDryRunPanel();
    // A Validate replaces the operator's picture of what is wrong; the prior submit's error
    // report describes rows that may no longer exist.
    this.hideErrorDownloadLinks();

    // The csv form carries no payload -- the grid's lives in the sibling form -- so an empty
    // file input would reach validate with no rows at all.
    if (this.currentUploadMethod === 'csv') {
      const fileInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="csv_file"]');
      if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
        this.showErrorMessages('No file uploaded. Please upload your file before validation.');
        return;
      }
    }

    const button = event.currentTarget as HTMLElement;
    const form = button.closest('form') as HTMLFormElement | null;
    if (!form) return;

    if (this.currentUploadMethod === 'handsontable') {
      document.dispatchEvent(new Event("gatherHandsontableData"));
    }

    const formData = new FormData(form);

    // per_form_csrf_tokens (load_defaults 7.0) binds the form's own token to @url + POST, and
    // this posts that FormData elsewhere -- only the meta tag's global token is valid here.
    fetch(this.tabConfig().urlValidate, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          this.renderSubmitErrors(form, data);
          return;
        }
        this.renderDryRunPanel(data as DryRunResult);
      })
      .catch(() => {
        this.showErrorMessages('An unexpected error occurred while validating.');
      });
  }

  // Only the dry-run tab renames its write: elsewhere the same channel message follows a
  // create, which "Update" would misdescribe.
  uploadSuccessHeading(): string {
    return this.tabConfig().dryRunUi ? 'Update - SUCCESS!' : 'Upload Successful!';
  }

  // Renders PlanPresenter's summary/rows/consequences generically, so a renamed/added
  // summary key or consequence code never needs a second client-side restatement.
  private renderDryRunPanel(data: DryRunResult) {
    if (!this.hasDryRunPanelTarget) return;

    const panel = this.dryRunPanelTarget;
    panel.innerHTML = '';

    // The dry run and the write share one success surface, so the summary has to say which
    // of the two produced it.
    const heading = document.createElement('div');
    heading.className = 'dry-run-panel__heading';
    heading.textContent = 'Validate - SUCCESS!';
    panel.appendChild(heading);

    const rows = data.rows ?? [];
    const changedRows = rows.filter(row => row.action !== 'noop');
    const noopCount = rows.length - changedRows.length;

    // Two counts per row, paired in the order the plan emits them (plan.rb summary_for).
    const summary = document.createElement('div');
    summary.className = 'dry-run-panel__summary';

    Object.entries(data.summary ?? {}).forEach(([key, value]) => {
      summary.appendChild(this.dryRunSummaryLine(this.humanize(key), value));
    });

    // Unchanged rows are filtered out of the list below, so their count has to survive here:
    // "row 4 matched and nothing changed" must stay distinguishable from "row 4 never parsed".
    if (noopCount > 0) {
      const line = this.dryRunSummaryLine('Rows not changed', noopCount);
      line.classList.add('dry-run-panel__summary-line--full');
      summary.appendChild(line);
    }

    if (summary.childElementCount > 0) panel.appendChild(summary);

    if (changedRows.length > 0) {
      const detailsHeading = document.createElement('div');
      detailsHeading.className = 'dry-run-panel__details-heading';
      detailsHeading.textContent = 'Details';
      panel.appendChild(detailsHeading);

      const rowsContainer = document.createElement('div');
      rowsContainer.className = 'dry-run-panel__rows';

      changedRows.slice(0, DRY_RUN_ROW_RENDER_CAP).forEach(row => {
        const line = document.createElement('div');
        line.className = 'dry-run-panel__row';
        const changes = (row.changes ?? []).join(', ');
        // Elections rows carry vote_id (no question_id); fall back rather than showing "question —".
        const target = row.question_id != null
          ? `question ${row.question_id}${row.answer_id ? `, answer ${row.answer_id}` : ''}`
          : row.vote_id != null ? `vote ${row.vote_id}` : '—';
        line.textContent = `Row ${row.row}: ${target} — ${row.action}` + (changes ? ` (${changes})` : '');
        rowsContainer.appendChild(line);
      });
      panel.appendChild(rowsContainer);

      const hidden = changedRows.length - DRY_RUN_ROW_RENDER_CAP;
      if (hidden > 0) {
        const remainder = document.createElement('div');
        remainder.className = 'dry-run-panel__row-remainder';
        remainder.textContent = `…and ${hidden} more row${hidden === 1 ? '' : 's'}`;
        panel.appendChild(remainder);
      }
    } else if (rows.length > 0) {
      // An empty rows box reads as a broken panel, and an all-noop dry run is common.
      const empty = document.createElement('div');
      empty.className = 'dry-run-panel__rows-empty';
      empty.textContent = `No changes detected — all ${rows.length} row${rows.length === 1 ? '' : 's'} already match`;
      panel.appendChild(empty);
    }

    (data.consequences ?? []).forEach(consequence => {
      const line = document.createElement('div');
      line.className = 'dry-run-panel__consequence';
      line.textContent = this.describeConsequence(consequence);
      panel.appendChild(line);
    });

    panel.classList.remove('hidden');
  }

  private hideDryRunPanel() {
    if (!this.hasDryRunPanelTarget) return;
    this.dryRunPanelTarget.classList.add('hidden');
    this.dryRunPanelTarget.innerHTML = '';
  }

  // Split into two spans so the count carries the weight on its own: a wall of fully bold
  // "Answers deleted: 0" lines buries the one number that is not zero.
  private dryRunSummaryLine(label: string, value: unknown): HTMLDivElement {
    const line = document.createElement('div');
    line.className = 'dry-run-panel__summary-line';

    const labelSpan = document.createElement('span');
    labelSpan.textContent = `${label}: `;

    const valueSpan = document.createElement('span');
    valueSpan.className = 'dry-run-panel__summary-value';
    valueSpan.textContent = String(value);

    line.append(labelSpan, valueSpan);
    return line;
  }

  private humanize(key: string): string {
    return key.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase());
  }

  // Every consequence hash shares `code` (plan.rb); the remaining keys vary by kind, so this
  // renders generically rather than re-deriving Plan's per-code shapes here.
  private describeConsequence(consequence: Record<string, unknown>): string {
    const { code, ...rest } = consequence;
    const details = Object.entries(rest).map(([key, value]) => `${key}: ${value}`).join(', ');
    return details ? `${code} (${details})` : String(code);
  }

  clearErrorMessages() {
    if (this.errorMessagesTarget) {
      this.errorMessagesTarget.innerHTML = "";
    }
  }

  // Feeds AgGridProvisionComponent's `handsontable:errors` listener. Pass `[]` on success
  // to clear stale error highlights left over from a prior failed submission.
  dispatchHandsontableErrors(errors: any[]) {
    const container = document.querySelector(".handsontable-wrapper");
    if (!container) return;

    const errorEvent = new CustomEvent("handsontable:errors", {
      detail: { errors },
      bubbles: true
    });
    container.dispatchEvent(errorEvent);
  }

  showErrorMessages(message: string) {
    if (this.errorMessagesTarget) {
      this.errorMessagesTarget.classList.remove('success-messages');
      this.errorMessagesTarget.classList.add('error-messages');
      this.errorMessagesTarget.innerHTML = message;
    }
  }

  showSuccessMessage(message: string) {
    if (this.errorMessagesTarget) {
      this.errorMessagesTarget.classList.remove('error-messages');
      this.errorMessagesTarget.classList.add('success-messages');
      this.errorMessagesTarget.innerHTML = message;
    }
  }

  injectDropdownWhenReady() {
    // Generic selector (not Stimulus-controller-specific): matches the grid widget's own
    // `.table-action-buttons` row inside `#handsontable-section`.
    const handsontableButtonSet = this.element.querySelector("#handsontable-section .table-action-buttons");

    if (handsontableButtonSet instanceof HTMLElement) {
      this.addButtons(handsontableButtonSet);
    }
  }

  addButtons(handsontableButtonSet: HTMLElement) {
    const dropdown = document.getElementById("dashboard-dropdown");
    if (!dropdown) return;

    const handsontableForm = document.querySelector(".handsontable-submit-container form");
    if (!handsontableForm) return;

    handsontableButtonSet.prepend(handsontableForm);
    handsontableButtonSet.appendChild(dropdown);

    dropdown.classList.remove('hidden');
  }

  toggleUploadMethod(event: Event) {
    this.clearErrorMessages();
    this.hideDownloadErrorOptions();

    const target = event.target as HTMLInputElement;

    if (!target) return;

    this.currentUploadMethod = target.value === 'csv' ? 'csv' : 'handsontable';
    this.repointStagedFilesForm();

    // Append radios live outside both forms — repoint each at whichever one is now active.
    const appendRadios = this.element.querySelectorAll<HTMLInputElement>('input[name="append"]');
    appendRadios.forEach(radio => {
      radio.setAttribute('form', target.value === 'csv' ? 'csv-provision-form' : 'handsontable-provision-form');
    });

    const handsontableSection = document.getElementById("handsontable-section");
    const csvUploadSection = document.getElementById("csv-upload-section");
    const dynamicColumnsPicker = document.getElementById("dynamic-columns-picker");

    if (!handsontableSection || !csvUploadSection) return;

    // Read the frame's config element per call — it is refreshed on every turbo-frame
    // navigation, unlike a Stimulus value on the (out-of-frame) controller root.
    const { loadTable, dynamicLanguageColumns } = this.tabConfig();
    const hasVoteIdControls = loadTable || dynamicLanguageColumns;

    const loadTableBtns = this.element.querySelectorAll<HTMLButtonElement>('.load-table-btn');

    if (target.value === "handsontable") {
      if (!hasVoteIdControls || this.tableRevealed) {
        handsontableSection.classList.remove("hidden");
      }
      csvUploadSection.classList.add("hidden");
      if (!hasVoteIdControls || this.tableRevealed) {
        dynamicColumnsPicker?.classList.remove("hidden");
      }
      loadTableBtns.forEach(btn => btn.classList.remove("hidden"));
    } else if (target.value === "csv") {
      handsontableSection.classList.add("hidden");
      csvUploadSection.classList.remove("hidden");
      dynamicColumnsPicker?.classList.add("hidden");
      loadTableBtns.forEach(btn => btn.classList.add("hidden"));
    }
  }

  toggleAppendMode(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target || !this.hasImportantTextTarget) return;

    this.importantTextTarget.textContent = target.dataset.bannerText ?? '';
  }

  clearFileInput() {
    const fileInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="csv_file"]');
    if (fileInput) {
      fileInput.value = "";
      this.removeButtonTarget.disabled = true;
    }

    this.clearErrorMessages();
  }

  fileSelected() {
    if (this.fileInputTarget.files && this.fileInputTarget.files.length > 0) {
      this.removeButtonTarget.disabled = false;
    } else {
      this.removeButtonTarget.disabled = true;
    }

    this.clearErrorMessages();
  }

  // ── Staged files ──────────────────────────────────────────────────────────

  // Target-lifecycle wiring rather than a data-action, so it survives turbo-frame re-renders.
  stagedFilesInputTargetConnected(input: HTMLInputElement) {
    input.addEventListener("change", this.stagedFilesChangeListener);
    input.setAttribute("form", this.activeProvisionFormId());
  }

  stagedFilesInputTargetDisconnected(input: HTMLInputElement) {
    input.removeEventListener("change", this.stagedFilesChangeListener);
    this.batchTransports.get(input)?.cancel();
    this.batchTransports.delete(input);
    this.batchChipRefs.delete(input);
    this.degradedInputs.delete(input);
    this.stagedFileSets.delete(input);
    this.stagedFolderNames.delete(input);
  }

  // The hidden staging_token field follows the staged file input's own
  // form-repointing exactly (mirrors stagedFilesInputTargetConnected).
  stagingTokenInputTargetConnected(input: HTMLInputElement) {
    input.setAttribute("form", this.activeProvisionFormId());
  }

  // Cancel aborts in-flight batches and DELETEs the session; no data-action in the
  // ERB (target-lifecycle wiring, same pattern as the staged file input above).
  stagedFilesBatchCancelTargetConnected(button: HTMLButtonElement) {
    button.addEventListener("click", this.batchCancelListener);
  }

  stagedFilesBatchCancelTargetDisconnected(button: HTMLButtonElement) {
    button.removeEventListener("click", this.batchCancelListener);
  }

  // A folder pick replaces the previous selection outright — the folder is the staging unit.
  private handleStagedFilesChanged(event: Event) {
    const input = event.target as HTMLInputElement;
    this.degradedInputs.delete(input);

    const rawFiles = Array.from(input.files ?? []);
    const { accepted, skipped, folderName } = this.filterFolderSelection(rawFiles);
    // Batched mode enforces the session caps locally (in addition to what the
    // module enforces per batch); off, the per-request caps are unchanged.
    const caps = this.stagingEnabledValue
      ? { maxFiles: this.sessionMaxFilesValue, maxTotalBytes: this.sessionMaxTotalBytesValue }
      : { maxFiles: this.maxFilesValue, maxTotalBytes: this.maxTotalBytesValue };
    const { error } = this.validateStagedSelection(accepted, caps);

    if (error) {
      // A folder pick is atomic — there is no prior good set to fall back to.
      if (this.stagingEnabledValue) this.batchTransports.get(input)?.cancel();
      this.stagedFileSets.set(input, []);
      this.stagedFolderNames.set(input, null);
      this.renderStagedList(input, []);
      this.renderSummary(input, [], null);
      const countedError = `${error} (folder contained ${rawFiles.length} file${rawFiles.length === 1 ? "" : "s"})`;
      this.renderCapsMessage(input, countedError);
      this.syncInputFiles(input, []);
    } else {
      this.stagedFileSets.set(input, accepted);
      this.stagedFolderNames.set(input, folderName);
      this.renderStagedList(input, accepted);
      this.renderSummary(input, accepted, folderName);
      this.renderSkippedMessage(input, skipped);

      if (this.stagingEnabledValue) {
        // Bytes never ride the form when batched — the module posts them itself
        // and the hidden staging_token field carries the session instead.
        this.syncInputFiles(input, []);
        const transport = this.batchTransportFor(input);
        transport.cancel(); // a fresh pick means a fresh session, not an append to the old one
        transport.start(accepted);
      } else {
        this.syncInputFiles(input, accepted);
      }
    }

    const finalFiles = this.stagedFileSets.get(input) ?? [];
    this.stagedNames = new Set(finalFiles.map((file) => this.normalizeStagedName(file.name)));
    this.dispatchStagedFilesChanged();
  }

  // A directory pick yields every file recursively; only top-level, non-hidden image/PDF
  // files are staged, the rest are reported as skipped.
  private filterFolderSelection(files: File[]): { accepted: File[]; skipped: SkippedEntry[]; folderName: string | null } {
    const accepted: File[] = [];
    const seen = new Set<string>();
    const skipped: SkippedEntry[] = [];
    let folderName: string | null = null;

    files.forEach((file) => {
      if (this.isIgnorableFolderEntry(file)) return;

      const reason = this.rejectionReasonFor(file);
      if (reason) {
        skipped.push({ name: this.skippedLabelFor(file), reason });
        return;
      }

      const name = this.normalizeStagedName(file.name);
      if (seen.has(name)) {
        // NFD/NFC collision — a folder can't hold two identical names, so keep the first.
        skipped.push({ name: this.skippedLabelFor(file), reason: "duplicate name" });
        return;
      }
      seen.add(name);
      if (folderName === null) folderName = this.folderNameFrom(file);
      accepted.push(file);
    });

    return { accepted, skipped, folderName };
  }

  // OS/OneDrive bookkeeping files are never candidate assets, so they aren't even reported
  // as skipped.
  private isIgnorableFolderEntry(file: File): boolean {
    const name = file.name.toLowerCase();
    return IGNORED_FOLDER_ENTRIES.has(name) || name.startsWith("._");
  }

  private rejectionReasonFor(file: File): string | null {
    const relPath = this.webkitRelativePathOf(file);
    if (relPath && relPath.split("/").length > 2) return "in a subfolder";
    if (file.name.startsWith(".")) return "hidden";
    if (!this.isTopLevelAcceptedFile(file)) return "unsupported type";
    return null;
  }

  // Subfolder entries keep their path inside the chosen folder so they can be found.
  private skippedLabelFor(file: File): string {
    const relPath = this.webkitRelativePathOf(file);
    if (!relPath) return file.name;
    const segments = relPath.split("/");
    return segments.length > 1 ? segments.slice(1).join("/") : file.name;
  }

  // Top level == exactly one "/" in webkitRelativePath (e.g. "candidates/alice.png").
  private isTopLevelAcceptedFile(file: File): boolean {
    const relPath = this.webkitRelativePathOf(file);
    if (!relPath || relPath.split("/").length !== 2) return false;
    if (file.name.startsWith(".")) return false;

    const dotIndex = file.name.lastIndexOf(".");
    if (dotIndex <= 0) return false;
    const ext = file.name.slice(dotIndex + 1).toLowerCase();
    return STAGED_FOLDER_EXTENSIONS.has(ext);
  }

  private folderNameFrom(file: File): string {
    const relPath = this.webkitRelativePathOf(file);
    return relPath?.split("/")[0] || "Selected folder";
  }

  private webkitRelativePathOf(file: File): string | undefined {
    return (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  }

  // Worded as information, not an error: a real candidate-photo folder commonly has strays.
  private renderSkippedMessage(input: HTMLInputElement, skipped: SkippedEntry[]) {
    const skippedCount = skipped.length;
    if (skippedCount === 0) {
      this.renderCapsMessage(input, null);
      return;
    }
    const target = this.capsMessageTargetFor(input);
    if (!target) return;

    // The count is plain text; only "View Details" is actionable, so the whole line is not a link.
    const count = document.createElement("span");
    count.className = "staged-files-skipped-count";
    count.textContent = `${skippedCount} file${skippedCount === 1 ? "" : "s"} skipped`;

    // The names live in the modal — a mixed folder can skip dozens, past what one line holds.
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "staged-files-skipped-trigger";
    trigger.textContent = "View Details";
    // The trigger lives inside the disclosure's <summary>; without this the click also toggles it.
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.showSkippedModal(skipped);
    });

    const pair = document.createElement("span");
    pair.className = "staged-files-skipped";
    pair.append(count, trigger);

    this.renderCapsMessage(input, null, "warning");
    target.classList.add("warning-messages");
    target.appendChild(pair);
    this.syncDisclosureVisibility(target);
  }

  // Local rather than the toolkit's showConfirmModal, which is a two-button decision dialog.
  private showSkippedModal(skipped: SkippedEntry[]) {
    const overlay = document.createElement("div");
    overlay.className = "toolkit-confirm-delete-modal__overlay staged-files-skipped-modal";
    overlay.innerHTML = `
      <div class="toolkit-confirm-delete-modal__dialog" role="alertdialog" aria-modal="true">
        <div class="toolkit-confirm-delete-modal__content">
          <div class="toolkit-confirm-delete-modal__icon toolkit-confirm-delete-modal__icon--warning">
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
          </div>
          <h3 class="toolkit-confirm-delete-modal__title"></h3>
          <p class="toolkit-confirm-delete-modal__message"></p>
          <ul class="staged-files-skipped-modal__list"></ul>
          <div class="toolkit-confirm-delete-modal__actions">
            <button type="button" class="toolkit-button-component toolkit-button-component--orange">OK</button>
          </div>
        </div>
      </div>
    `;

    const plural = skipped.length === 1 ? "" : "s";
    (overlay.querySelector(".toolkit-confirm-delete-modal__title") as HTMLElement).textContent =
      `${skipped.length} file${plural} skipped`;
    (overlay.querySelector(".toolkit-confirm-delete-modal__message") as HTMLElement).textContent =
      `${skipped.length === 1 ? "This file was" : "These files were"} not staged. Everything else in the folder was.`;

    const list = overlay.querySelector(".staged-files-skipped-modal__list") as HTMLElement;
    skipped.forEach((entry) => {
      const item = document.createElement("li");
      const name = document.createElement("span");
      name.className = "staged-files-skipped-modal__name";
      // textContent throughout: these are filenames off the user's disk, never markup.
      name.textContent = entry.name;
      const reason = document.createElement("span");
      reason.className = "staged-files-skipped-modal__reason";
      reason.textContent = entry.reason;
      item.append(name, reason);
      list.appendChild(item);
    });

    const close = () => {
      overlay.remove();
      document.removeEventListener("keydown", onEsc);
    };
    const onEsc = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };

    document.addEventListener("keydown", onEsc);
    overlay.addEventListener("click", (event) => { if (event.target === overlay) close(); });
    overlay.querySelector("button")!.addEventListener("click", close);

    document.body.appendChild(overlay);
    (overlay.querySelector("button") as HTMLButtonElement).focus();
  }

  // Keeps the native FileList in step with the committed array, so submitted FormData
  // matches what the UI shows.
  private syncInputFiles(input: HTMLInputElement, files: File[]) {
    try {
      const dt = new DataTransfer();
      files.forEach((file) => dt.items.add(file));
      input.files = dt.files;
    } catch {
      // Some browsers refuse programmatic FileList assignment; the chip list still holds.
    }
    if (files.length === 0) input.value = "";
  }

  private normalizeStagedName(name: string): string {
    return name.replace(ASCII_TRIM, "").normalize("NFC");
  }

  // `caps` is Phase 1's MAX_FILES/MAX_TOTAL_BYTES when the flag is off, or the session
  // caps when batched — the per-file cap and duplicate check never change.
  private validateStagedSelection(
    files: File[],
    caps: { maxFiles: number; maxTotalBytes: number }
  ): { names: string[]; error: string | null } {
    const names = files.map((file) => this.normalizeStagedName(file.name));

    if (files.length > caps.maxFiles) {
      return { names: [], error: `Too many files staged (max ${caps.maxFiles}); ${files.length} selected` };
    }

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes > caps.maxTotalBytes) {
      return {
        names: [],
        error: `Total staged size ${this.formatBytes(totalBytes)} exceeds the ${this.formatBytes(caps.maxTotalBytes)} limit`
      };
    }

    const oversized = names.filter((_, index) => files[index].size > this.maxFileBytesValue);
    if (oversized.length > 0) {
      return { names: [], error: `Over the ${this.formatBytes(this.maxFileBytesValue)} per-file limit: ${oversized.join(", ")}` };
    }

    const seen = new Set<string>();
    const duplicates = new Set<string>();
    names.forEach((name) => {
      if (seen.has(name)) duplicates.add(name);
      seen.add(name);
    });
    if (duplicates.size > 0) {
      return { names: [], error: `Duplicate staged file name: ${Array.from(duplicates).join(", ")}` };
    }

    return { names, error: null };
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`;
  }

  private sectionFor(input: HTMLInputElement): Element | null {
    return input.closest(".staged-files-section");
  }

  private listTargetFor(input: HTMLInputElement): HTMLElement | undefined {
    const section = this.sectionFor(input);
    return section ? this.stagedFilesListTargets.find((target) => section.contains(target)) : undefined;
  }

  private capsMessageTargetFor(input: HTMLInputElement): HTMLElement | undefined {
    const section = this.sectionFor(input);
    return section ? this.stagedFilesCapsMessageTargets.find((target) => section.contains(target)) : undefined;
  }

  private summaryTargetFor(input: HTMLInputElement): HTMLElement | undefined {
    const section = this.sectionFor(input);
    return section ? this.stagedFilesSummaryTargets.find((target) => section.contains(target)) : undefined;
  }

  private clearTargetFor(input: HTMLInputElement): HTMLElement | undefined {
    const section = this.sectionFor(input);
    return section ? this.stagedFilesClearTargets.find((target) => section.contains(target)) : undefined;
  }

  private inputForSection(section: Element): HTMLInputElement | undefined {
    return this.stagedFilesInputTargets.find((target) => section.contains(target));
  }

  // The input sits outside both forms, so its form owner must follow the active mode or
  // rails-ujs drops the files from the submitted FormData.
  private activeProvisionFormId(): string {
    return this.currentUploadMethod === "csv" ? "csv-provision-form" : "handsontable-provision-form";
  }

  private repointStagedFilesForm() {
    const formId = this.activeProvisionFormId();
    this.stagedFilesInputTargets.forEach((input) => input.setAttribute("form", formId));
    // The hidden staging_token field follows the file input's own form repointing.
    this.stagingTokenInputTargets.forEach((input) => input.setAttribute("form", formId));
  }

  private handsontableStagedInput(): HTMLInputElement | undefined {
    return this.stagedFilesInputTargets[0];
  }

  private renderCapsMessage(input: HTMLInputElement, message: string | null, kind: "error" | "warning" = "error") {
    const target = this.capsMessageTargetFor(input);
    if (!target) return;

    target.textContent = message ?? "";
    target.classList.remove("error-messages", "warning-messages");
    if (message) target.classList.add(kind === "warning" ? "warning-messages" : "error-messages");
    this.syncDisclosureVisibility(target);
  }

  private renderStagedList(input: HTMLInputElement, files: File[]) {
    const target = this.listTargetFor(input);
    if (!target) return;

    target.innerHTML = "";
    const chipRefs = new Map<string, BatchChipRefs>();

    files.forEach((file) => {
      const name = this.normalizeStagedName(file.name);

      const chip = document.createElement("span");
      chip.className = "staged-files-list__chip";
      chip.dataset.fileName = name;

      const label = document.createElement("span");
      label.className = "staged-files-list__chip-label";
      label.textContent = `${name} (${this.formatBytes(file.size)})`;
      chip.appendChild(label);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "staged-files-list__chip-remove";
      remove.textContent = "×";
      remove.setAttribute("aria-label", `Remove ${name}`);
      remove.addEventListener("click", () => this.removeStagedFile(input, file));
      chip.appendChild(remove);

      if (this.stagingEnabledValue && !this.degradedInputs.has(input)) {
        const refs = this.appendBatchChipControls(input, chip, name);
        if (refs) chipRefs.set(name, refs);
      }

      target.appendChild(chip);
    });

    this.batchChipRefs.set(input, chipRefs);
    this.updateDisclosure(target, files.length);
  }

  // Clones the per-chip batch-state/progress/retry/remove template
  // onto one file's chip; returns the live refs so batch snapshots can update them in place.
  private appendBatchChipControls(input: HTMLInputElement, chip: HTMLElement, name: string): BatchChipRefs | null {
    const template = this.batchChipTemplateFor(input);
    if (!template) return null;

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const stateEl = fragment.querySelector<HTMLElement>(".staged-files-list__chip-batch-state");
    const progressEl = fragment.querySelector<HTMLElement>(".staged-files-list__chip-batch-progress");
    const progressBarEl = fragment.querySelector<HTMLElement>(".staged-files-list__chip-batch-progress-bar");
    const retryBtn = fragment.querySelector<HTMLButtonElement>(".staged-files-list__chip-batch-retry");
    const removeBtn = fragment.querySelector<HTMLButtonElement>(".staged-files-list__chip-batch-remove");
    if (!stateEl || !progressEl || !progressBarEl || !retryBtn || !removeBtn) return null;

    chip.appendChild(fragment);
    return { stateEl, progressEl, progressBarEl, retryBtn, removeBtn };
  }

  private batchChipTemplateFor(input: HTMLInputElement): HTMLTemplateElement | undefined {
    const section = this.sectionFor(input);
    return section
      ? this.stagedFilesBatchChipTemplateTargets.find((target) => section.contains(target))
      : undefined;
  }

  // The chip list sits in a <details> so dozens of names can't push the grid down the page.
  private updateDisclosure(listTarget: HTMLElement, count: number) {
    const details = listTarget.closest("details");
    if (!(details instanceof HTMLDetailsElement)) return;

    const label = details.querySelector(".staged-files-disclosure__label");
    if (label) {
      label.textContent = `Files (${count})`;
      label.classList.toggle("hidden", count === 0);
    }

    // Never auto-opens — a fresh pick shows the count and leaves the names behind the toggle.
    if (count === 0) details.open = false;
    this.syncDisclosureVisibility(details);
  }

  // The <details> also carries the caps/skipped message, so it stays on screen (toggle inert)
  // when there is a message but nothing staged.
  private syncDisclosureVisibility(node: Element) {
    const details = node.closest("details");
    if (!(details instanceof HTMLDetailsElement)) return;

    const labelHidden = details.querySelector(".staged-files-disclosure__label")?.classList.contains("hidden") ?? true;
    const message = details.querySelector(".staged-files-caps-message");
    const hasMessage = (message?.textContent ?? "").trim() !== "";

    details.classList.toggle("hidden", labelHidden && !hasMessage);
    details.classList.toggle("staged-files-disclosure--message-only", labelHidden);
    if (labelHidden) details.open = false;
  }

  private renderSummary(input: HTMLInputElement, files: File[], folderName: string | null) {
    const summary = this.summaryTargetFor(input);
    const clear = this.clearTargetFor(input);
    if (!summary || !clear) return;

    if (files.length === 0) {
      summary.textContent = "";
      summary.classList.add("hidden");
      clear.classList.add("hidden");
      return;
    }

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const label = folderName ?? "Selected folder";
    summary.textContent = `${label} · ${files.length} file${files.length === 1 ? "" : "s"} · ${this.formatBytes(totalBytes)}`;
    summary.classList.remove("hidden");
    clear.classList.remove("hidden");
  }

  // Removing a chip can only shrink the set, so the caps/duplicate validation is skipped.
  private removeStagedFile(input: HTMLInputElement, file: File) {
    const name = this.normalizeStagedName(file.name);
    const remaining = (this.stagedFileSets.get(input) ?? []).filter((candidate) => candidate !== file);
    this.stagedFileSets.set(input, remaining);
    this.renderStagedList(input, remaining);
    this.renderSummary(input, remaining, this.stagedFolderNames.get(input) ?? null);
    this.renderCapsMessage(input, null);
    // Batched mode never resurfaces bytes on the input — only the token submits.
    this.syncInputFiles(input, this.stagingEnabledValue && !this.degradedInputs.has(input) ? [] : remaining);
    this.stagedNames = new Set(remaining.map((f) => this.normalizeStagedName(f.name)));
    this.dispatchStagedFilesChanged();

    // Batched mode: the "×" also drops the file from its batch / DELETEs it server-side so
    // the staged-file view and the session never diverge (also the cloned template's Remove button).
    if (this.stagingEnabledValue && !this.degradedInputs.has(input)) {
      this.batchTransports.get(input)?.removeFiles([name]);
    }
  }

  clearStagedFilesClick(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const section = button.closest(".staged-files-section");
    const input = section ? this.inputForSection(section) : undefined;
    if (!input) return;

    // Clear on the picker cancels the session too.
    if (this.stagingEnabledValue) this.batchTransports.get(input)?.cancel();
    this.clearStagedInput(input);
  }

  // The dedicated Cancel button aborts in-flight batches, DELETEs the session
  // and clears local state.
  private handleBatchCancelClick(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const section = button.closest(".staged-files-section");
    const input = section ? this.inputForSection(section) : undefined;
    if (!input) return;

    this.batchTransports.get(input)?.cancel();
    this.clearStagedInput(input);
  }

  private clearStagedInput(input: HTMLInputElement) {
    this.stagedFileSets.set(input, []);
    this.stagedFolderNames.set(input, null);
    this.renderStagedList(input, []);
    this.renderSummary(input, [], null);
    this.renderCapsMessage(input, null);
    this.syncInputFiles(input, []);
    this.stagedNames = new Set();
    this.dispatchStagedFilesChanged();
  }

  // ── Staged upload batches transport ───────────────────────────────────────

  private batchTransportFor(input: HTMLInputElement): StagedUploadBatches {
    let transport = this.batchTransports.get(input);
    if (transport) return transport;

    const config: StagedUploadBatchesConfig = {
      openPath: this.stagingOpenPathValue,
      batchesPath: this.stagingBatchesPathValue,
      filesPath: this.stagingFilesPathValue,
      sessionPath: this.stagingCancelPathValue,
      batchMaxFiles: this.batchMaxFilesValue,
      batchMaxBytes: this.batchMaxBytesValue,
      batchConcurrency: this.batchConcurrencyValue,
      onBatchesChanged: (batches) => this.handleBatchesChanged(input, batches),
      onSessionInvalid: (message) => this.handleSessionInvalid(input, message),
      onOpenFailed: (message) => this.handleOpenFailed(input, message)
    };
    transport = new StagedUploadBatches(config);
    this.batchTransports.set(input, transport);
    return transport;
  }

  // Drives chip UI, submit gating, the Cancel affordance and the degrade-to-Phase-1
  // path from every batch state change the module reports.
  private handleBatchesChanged(input: HTMLInputElement, snapshots: StagedUploadBatchSnapshot[]) {
    this.updateBatchChipsUI(input, snapshots);
    this.syncStagingTokenValue(input);
    this.updateSubmitAvailability(input);
    this.updateBatchCancelVisibility(input, snapshots);
    input.disabled = snapshots.some((batch) => batch.status !== "staged");

    // Only a transport or server failure (exhausted retries) triggers the degrade path —
    // a validation ("Inspect") failure needs Remove/fix, never a same-request fallback.
    const transportFailure = snapshots.find(
      (batch) => batch.status === "failed" && (batch.failureReason === "transport" || batch.failureReason === "server")
    );
    if (!transportFailure) return;

    const files = this.stagedFileSets.get(input) ?? [];
    if (this.fitsPhase1Caps(files)) {
      this.degradeToPhase1(input, "A batch failed to upload after retrying.");
    } else {
      this.renderCapsMessage(
        input,
        "A batch failed to upload after retrying. Retry the failed files above, or Clear and pick fewer.",
        "warning"
      );
    }
  }

  private updateBatchChipsUI(input: HTMLInputElement, snapshots: StagedUploadBatchSnapshot[]) {
    const refs = this.batchChipRefs.get(input);
    if (!refs) return;

    snapshots.forEach((batch) => {
      batch.names.forEach((name) => {
        const chipRefs = refs.get(name);
        if (!chipRefs) return;

        chipRefs.stateEl.textContent = this.batchStateLabel(batch);
        chipRefs.progressEl.setAttribute("aria-valuenow", String(batch.progressPercent));
        chipRefs.progressBarEl.style.width = `${batch.progressPercent}%`;
        chipRefs.retryBtn.classList.toggle("hidden", !batch.canRetry);
        chipRefs.retryBtn.onclick = () => this.batchTransports.get(input)?.retryBatch(batch.id);
        chipRefs.removeBtn.classList.toggle("hidden", !batch.offerRemove);
        chipRefs.removeBtn.onclick = () => {
          const file = (this.stagedFileSets.get(input) ?? []).find((f) => this.normalizeStagedName(f.name) === name);
          if (file) this.removeStagedFile(input, file);
        };
      });
    });
  }

  private batchStateLabel(batch: StagedUploadBatchSnapshot): string {
    switch (batch.status) {
      case "queued": return "Queued";
      case "uploading": return `Uploading ${batch.progressPercent}%`;
      case "staged": return "Staged";
      case "failed": return batch.message ?? "Failed";
      default: return "";
    }
  }

  // Submit is disabled while any batch is queued, uploading or failed.
  private updateSubmitAvailability(input: HTMLInputElement) {
    const pending = (this.batchTransports.get(input)?.pendingNames().length ?? 0) > 0;
    this.submitButtons().forEach((button) => { button.disabled = pending; });
  }

  // `f.submit` renders an <input type="submit">, not a <button> -- both selectors are needed.
  private submitButtons(): (HTMLButtonElement | HTMLInputElement)[] {
    return Array.from(
      this.element.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
        '.handsontable-submit-container button[type="submit"], .handsontable-submit-container input[type="submit"], ' +
        '.csv-upload-form button[type="submit"], .csv-upload-form input[type="submit"]'
      )
    );
  }

  // The pre-check blocks with a message naming the files not yet staged.
  private blockSubmitForPendingBatches(): boolean {
    const input = this.handsontableStagedInput();
    const transport = input ? this.batchTransports.get(input) : undefined;
    if (!transport) return false;

    const pending = transport.pendingNames();
    if (pending.length === 0) return false;

    this.showErrorMessages(`Still uploading: ${pending.join(", ")}. Wait for every staged file to finish before submitting.`);
    return true;
  }

  private updateBatchCancelVisibility(input: HTMLInputElement, snapshots: StagedUploadBatchSnapshot[]) {
    const button = this.batchCancelTargetFor(input);
    if (!button) return;
    button.classList.toggle("hidden", !snapshots.some((batch) => batch.status !== "staged"));
  }

  private batchCancelTargetFor(input: HTMLInputElement): HTMLElement | undefined {
    const section = this.sectionFor(input);
    return section ? this.stagedFilesBatchCancelTargets.find((target) => section.contains(target)) : undefined;
  }

  private syncStagingTokenValue(input: HTMLInputElement) {
    const tokenInput = this.stagingTokenInputTargetFor(input);
    if (tokenInput) tokenInput.value = this.batchTransports.get(input)?.currentToken() ?? "";
  }

  private stagingTokenInputTargetFor(input: HTMLInputElement): HTMLInputElement | undefined {
    const section = this.sectionFor(input);
    return section ? this.stagingTokenInputTargets.find((target) => section.contains(target)) : undefined;
  }

  // Drop the session, tell the operator, re-enable the picker.
  private handleSessionInvalid(input: HTMLInputElement, message: string) {
    this.batchTransports.delete(input);
    this.batchChipRefs.delete(input);
    this.degradedInputs.delete(input);
    this.stagedFileSets.set(input, []);
    this.stagedFolderNames.set(input, null);
    this.renderStagedList(input, []);
    this.renderSummary(input, [], null);
    this.syncInputFiles(input, []);
    this.stagedNames = new Set();
    this.dispatchStagedFilesChanged();

    const tokenInput = this.stagingTokenInputTargetFor(input);
    if (tokenInput) tokenInput.value = "";

    input.disabled = false;
    this.updateBatchCancelVisibility(input, []);
    this.renderCapsMessage(input, `${message} The upload session expired -- please re-select the folder to re-stage the files.`);
  }

  // Opening the session failed outright -- no batch object exists to retry, so degrade if
  // the whole set still fits one Phase 1 request; otherwise leave the picker for retry.
  private handleOpenFailed(input: HTMLInputElement, message: string) {
    const files = this.stagedFileSets.get(input) ?? [];
    if (this.fitsPhase1Caps(files)) {
      this.degradeToPhase1(input, `Could not start batched upload (${message}).`);
      return;
    }

    input.disabled = false;
    this.renderCapsMessage(
      input,
      `Could not start batched upload (${message}). Re-select the folder to retry, or remove files to fit a single upload.`
    );
  }

  private fitsPhase1Caps(files: File[]): boolean {
    if (files.length > this.maxFilesValue) return false;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    return totalBytes <= this.maxTotalBytesValue;
  }

  // Cancel the batched session and fall back to letting the bytes ride the form
  // like Phase 1, since the whole set fits a single request.
  private degradeToPhase1(input: HTMLInputElement, reason: string) {
    const files = this.stagedFileSets.get(input) ?? [];

    this.batchTransports.get(input)?.cancel();
    this.batchTransports.delete(input);
    this.batchChipRefs.delete(input);
    this.degradedInputs.add(input);

    const tokenInput = this.stagingTokenInputTargetFor(input);
    if (tokenInput) tokenInput.value = "";

    input.disabled = false;
    this.renderStagedList(input, files); // no transport left -- chips drop back to Phase 1 controls
    this.updateBatchCancelVisibility(input, []);
    this.updateSubmitAvailability(input);
    this.syncInputFiles(input, files);
    this.renderCapsMessage(input, `${reason} Falling back to a single direct upload for these files.`, "warning");
  }

  private dispatchStagedFilesChanged() {
    const names = Array.from(this.stagedNames);
    this.element.dispatchEvent(new CustomEvent(STAGED_FILES_CHANGED_EVENT, {
      detail: { names, files: names.map((name) => ({ name, extension: this.extensionOf(name) })) },
      bubbles: true
    }));
  }

  // Mirrors StagedFiles::Inspect#extension_of — File.extname semantics, so a leading dot is
  // not an extension.
  private extensionOf(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot <= 0 ? "" : name.slice(dot + 1).toLowerCase();
  }

  // A deliberate subset of attachment_value.rb, which alone owns the INVALID/length rules.
  // Parity table: CLASSIFICATION_CASES in test/models/questions/provision/attachment_value_test.rb.
  private isFilenameCandidate(raw: string | undefined | null): raw is string {
    if (raw === undefined || raw === null) return false;
    const trimmed = this.normalizeStagedName(raw);
    if (trimmed === "") return false;
    if (trimmed.includes("://")) return false;
    if (trimmed.startsWith("eb:attachment/")) return false;
    return true;
  }

  // Returns true when submission should be blocked; also raises the non-blocking
  // unreferenced-file warning. An empty staged set runs no pre-check at all.
  private runGridAttachmentPreCheck(): boolean {
    const input = this.handsontableStagedInput();
    const stagedFiles = input ? (this.stagedFileSets.get(input) ?? []) : [];
    const stagedSet = new Set(stagedFiles.map((file) => this.normalizeStagedName(file.name)));
    if (stagedSet.size === 0) return false;

    const errors: FieldCentricError[] = [];
    const referenced = new Set<string>();

    this.lastGatheredRows.forEach((row, index) => {
      ATTACHMENT_COLUMNS.forEach((column) => {
        const raw = row[column];
        if (!this.isFilenameCandidate(raw)) return;

        const name = this.normalizeStagedName(raw);
        referenced.add(name);

        if (!stagedSet.has(name)) {
          errors.push({ rows: [index + 1], field: column, message: `'${name}' is not among the staged files.` });
        }
      });
    });

    if (errors.length > 0) {
      this.dispatchHandsontableErrors(errors);
      this.showErrorMessages("Please fix the highlighted attachment cells before submitting.");
      return true;
    }

    if (input) {
      const unreferenced = Array.from(stagedSet).filter((name) => !referenced.has(name));
      if (unreferenced.length > 0) {
        this.renderCapsMessage(input, `Staged but not referenced by any cell: ${unreferenced.join(", ")}`, "warning");
      } else {
        this.renderCapsMessage(input, null);
      }
    }

    return false;
  }

  showDownloadErrorOptions() {
    const csvLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-csv');
    csvLinks.forEach(link => link.classList.remove('hidden'));

    const xlsxLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-xlsx');
    xlsxLinks.forEach(link => link.classList.remove('hidden'));
  }

  hideDownloadErrorOptions() {
    this.hideErrorDownloadLinks();

    this.uploadId = null;
  }

  // Hides the links without discarding uploadId, so a Validate run can stop offering the
  // previous report while leaving it retrievable (callers ending the upload use hideDownloadErrorOptions).
  private hideErrorDownloadLinks() {
    const csvLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-csv');
    csvLinks.forEach(link => link.classList.add('hidden'));

    const xlsxLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-xlsx');
    xlsxLinks.forEach(link => link.classList.add('hidden'));
  }

  downloadErrorCsv(event: Event) {
    event.preventDefault();

    if (!this.uploadId) {
      console.error("No upload ID available for error CSV download.");
      return;
    }

    const url = `/admin/api_dashboard_download/errors?upload_id=${this.uploadId}&report_format=csv`;

    window.location.href = url;
  }

  downloadErrorXlsx() {
    if (!this.uploadId) {
      console.error("No upload ID available for error XLSX download.");
      return;
    }

    const url = `/admin/api_dashboard_download/errors?upload_id=${this.uploadId}&report_format=xlsx`;

    window.location.href = url;
  }

  // ── Language API: Read tab ────────────────────────────────────────────────

  preloadLanguages() {
    this.clearSingleErrorMessages();

    if (!this.hasLanguagePreloadVoteIdTarget) return;
    const voteId = this.languagePreloadVoteIdTarget.value.trim();

    if (!voteId) {
      this.showSingleErrorMessage('Please enter a Vote ID.');
      return;
    }

    const preloadPath = this.tabConfig().preloadPath;
    if (!preloadPath) {
      this.showSingleErrorMessage('Preload path not configured.');
      return;
    }

    const url = new URL(preloadPath, window.location.origin);
    url.searchParams.set('vote_id', voteId);

    fetch(url.toString(), {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async response => {
        if (!response.ok) {
          const data: { error?: string } = await response.json();
          this.showSingleErrorMessage(data.error || 'Failed to load languages.');
          return;
        }
        const data: { vote_id: string; secondary_languages: Array<{ code: string; name: string }> } = await response.json();
        this.populatePreloadSelect(data.secondary_languages);
      })
      .catch(() => {
        this.showSingleErrorMessage('An unexpected error occurred while loading languages.');
      });
  }

  downloadAndProceed() {
    if (!this.hasLanguagePreloadVoteIdTarget || !this.hasLanguagePreloadSelectTarget) return;

    const voteId = this.languagePreloadVoteIdTarget.value.trim();
    if (!voteId) {
      this.showSingleErrorMessage('Please enter a Vote ID.');
      return;
    }

    const select = this.languagePreloadSelectTarget;
    const selectedCodes = Array.from(select.options).filter(o => o.selected).map(o => o.value);

    if (selectedCodes.length === 0) {
      this.showSingleErrorMessage('Please select at least one language.');
      return;
    }

    const downloadPath = this.tabConfig().downloadUrl;
    if (!downloadPath) {
      this.showSingleErrorMessage('Download path not configured.');
      return;
    }

    const downloadUrl = new URL(downloadPath, window.location.origin);
    downloadUrl.searchParams.set('vote_id', voteId);
    selectedCodes.forEach(code => downloadUrl.searchParams.append('languages[]', code));

    const link = document.createElement('a');
    link.href = downloadUrl.toString();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.showSingleSuccessMessage('Download successful!');
  }

  private populatePreloadSelect(languages: Array<{ code: string; name: string }>) {
    if (!this.hasLanguagePreloadSelectTarget) return;

    const select = this.languagePreloadSelectTarget;

    // Destroy only if select2 is actually active on this element
    if ($(select).data('select2')) {
      try { $(select).select2('destroy'); } catch (_) { /* noop */ }
    }

    select.innerHTML = '';
    languages.forEach(lang => {
      const option = document.createElement('option');
      option.value = lang.code;
      option.text = lang.name;
      option.selected = true;
      select.appendChild(option);
    });

    $(select).select2({ placeholder: 'Select languages...' });

    // Use flex, not just unhidden, so the inline layout works.
    if (this.hasLanguagePickerSectionTarget) {
      this.languagePickerSectionTarget.classList.remove('hidden');
      this.languagePickerSectionTarget.style.display = 'flex';
    }

    if (this.hasDownloadSubmitButtonTarget) {
      this.downloadSubmitButtonTarget.classList.remove('hidden');
    }
  }

  // ── Generic grid tabs: load_table / dynamic_language_columns ─────────────

  syncVoteId(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const hotVoteIdField = document.getElementById('handsontable-vote-id-field') as HTMLInputElement | null;
    if (hotVoteIdField) hotVoteIdField.value = value;

    const csvVoteIdField = document.getElementById('csv-vote-id-field') as HTMLInputElement | null;
    if (csvVoteIdField) csvVoteIdField.value = value;
  }

  // Reads the in-frame config element rather than a Stimulus value, so it always reflects
  // the tab currently rendered inside the turbo-frame (see apis_dashboards_component.html.erb).
  private tabConfig(): { loadTable: boolean; dynamicLanguageColumns: boolean; preloadPath: string; downloadUrl: string; urlValidate: string; dryRunUi: boolean } {
    const el = document.getElementById('dashboard-tab-config') as HTMLElement | null;
    return {
      loadTable: el?.dataset.loadTable === 'true',
      dynamicLanguageColumns: el?.dataset.dynamicLanguageColumns === 'true',
      preloadPath: el?.dataset.preloadPath ?? '',
      downloadUrl: el?.dataset.downloadUrl ?? '',
      urlValidate: el?.dataset.urlValidate ?? '',
      dryRunUi: el?.dataset.dryRunUi === 'true'
    };
  }

  private revealGrid() {
    document.getElementById('handsontable-section')?.classList.remove('hidden');
    document.querySelector('.handsontable-submit-container')?.classList.remove('hidden');
    this.getHandsontableController()?.refresh();
    this.tableRevealed = true;
  }

  // Single entry point for every "Load Table" button: runs the
  // dynamic-columns pre-step when the tab declares it, then loads rows when load_table is on.
  loadTable() {
    this.clearErrorMessages();
    if (!this.hasVoteIdInputTarget) return;

    const voteId = this.voteIdInputTarget.value.trim();
    if (!voteId) {
      this.showErrorMessages('Please enter a Vote ID.');
      return;
    }

    if (this.tabConfig().dynamicLanguageColumns) {
      this.loadDynamicLanguageColumns(voteId, () => this.loadTableRows(voteId));
    } else {
      this.loadTableRows(voteId);
    }
  }

  // The languages `create` tab has dynamic_language_columns without load_table: it only
  // needs the picker/columns pre-step, so this reveals an empty grid instead of fetching rows.
  private loadTableRows(voteId: string) {
    const { loadTable, dynamicLanguageColumns, downloadUrl } = this.tabConfig();
    if (!loadTable) {
      this.revealGrid();
      return;
    }

    if (!downloadUrl) {
      this.showErrorMessages('Download path not configured.');
      return;
    }

    const url = new URL(downloadUrl, window.location.origin);
    url.searchParams.set('vote_id', voteId);
    url.searchParams.set('html', 'false');
    if (dynamicLanguageColumns) {
      this.currentLanguageColumns.forEach(column => url.searchParams.append('languages[]', column.key));
    }

    fetch(url.toString(), {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async response => {
        if (!response.ok) {
          const data: { error?: string } = await response.json().catch(() => ({}) as { error?: string });
          this.showErrorMessages(data.error || 'Failed to load the table.');
          return;
        }
        const data: { rows?: Array<Record<string, any>> } = await response.json();
        this.revealGrid();
        this.getHandsontableController()?.loadRows(data.rows ?? []);
      })
      .catch(() => {
        this.showErrorMessages('An unexpected error occurred while loading the table.');
      });
  }

  // Fetches secondary languages, seats the select2 picker and captures the grid's base
  // columns; `onComplete` runs only once the dynamic columns are in place (language-specific).
  private loadDynamicLanguageColumns(voteId: string, onComplete: () => void) {
    const preloadPath = this.tabConfig().preloadPath;
    if (!preloadPath) {
      this.showErrorMessages('Preload path not configured.');
      return;
    }

    const url = new URL(preloadPath, window.location.origin);
    url.searchParams.set('vote_id', voteId);

    fetch(url.toString(), {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async response => {
        if (!response.ok) {
          const data: { error?: string } = await response.json().catch(() => ({}) as { error?: string });
          this.showErrorMessages(data.error || 'Failed to load languages.');
          return;
        }
        const data: { secondary_languages: Array<{ code: string; name: string }> } = await response.json();
        this.errorMessagesTarget.innerHTML = '';

        const htController = this.getHandsontableController();
        if (htController && this.baseHandsontableColumns.length === 0) {
          this.baseHandsontableColumns = this.baseColumnsFrom(htController);
        }

        const allCodes = data.secondary_languages.map(l => l.code);
        this.currentLanguageColumns = [];
        this.initCreateSelect2(data.secondary_languages, allCodes);
        document.getElementById('dynamic-columns-picker')?.classList.remove('hidden');

        onComplete();
      })
      .catch(() => {
        this.showErrorMessages('An unexpected error occurred while loading languages.');
      });
  }

  private initCreateSelect2(
    availableLanguages: Array<{ code: string; name: string }>,
    selectedCodes: string[] = []
  ) {
    if (!this.hasLanguageCreateSelectTarget) return;

    const select = this.languageCreateSelectTarget;

    // Destroy only if select2 is actually active on this element
    if ($(select).data('select2')) {
      try { $(select).select2('destroy'); } catch (_) { /* noop */ }
    }

    select.innerHTML = '';
    availableLanguages.forEach(lang => {
      const option = document.createElement('option');
      option.value = lang.code;
      option.text = lang.name;
      option.selected = selectedCodes.includes(lang.code);
      select.appendChild(option);
    });

    const $select = $(select).select2({ placeholder: 'Select languages...' });

    $select.on('select2:select', (e: any) => {
      const code = e.params.data.id as string;
      const name = e.params.data.text as string;
      this.currentLanguageColumns.push({ key: code, header: name });
      this.rebuildHandsontable();
    });

    $select.on('select2:unselect', (e: any) => {
      const code = e.params.data.id as string;
      this.currentLanguageColumns = this.currentLanguageColumns.filter(c => c.key !== code);
      this.rebuildHandsontable();
    });

    this.currentLanguageColumns = selectedCodes
      .map(code => {
        const lang = availableLanguages.find(l => l.code === code);
        return lang ? { key: code, header: lang.name } : null;
      })
      .filter((c): c is { key: string; header: string } => c !== null);

    if (this.currentLanguageColumns.length > 0) {
      this.rebuildHandsontable();
    }
  }

  private rebuildHandsontable() {
    const htController = this.getHandsontableController();
    if (!htController || !htController.isReady()) {
      // Grid controller not ready yet — retry once after another frame
      requestAnimationFrame(() => setTimeout(() => this.rebuildHandsontable(), 50));
      return;
    }

    const allColumns = [...this.baseHandsontableColumns, ...this.currentLanguageColumns];
    htController.rebuildColumns(allColumns);
  }

  // The grid widget is AgGridProvisionComponent; legacy "handsontable" naming is kept
  // deliberately (it is the DOM/event contract the whole dashboard shares).
  private getHandsontableController() {
    const wrapper = this.element.querySelector('.handsontable-wrapper');
    if (!wrapper) return null;
    return this.application.getControllerForElementAndIdentifier(
      wrapper,
      'admin--apis-dashboards--ag-grid-provision-component--ag-grid-provision-component'
    ) as any;
  }

  // ColDef colId/field == schema key, headerName == display header (see ag_grid_column_defs).
  private baseColumnsFrom(htController: any): Array<{ key: string; header: string }> {
    return (htController.columnDefsValue ?? []).map((colDef: any) => ({
      key: colDef.colId ?? colDef.field,
      header: colDef.headerName
    }));
  }
}
